import { Alert, Box, Link, Stack } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmberOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { usePermissions } from '../../../contexts/AuthContext';
import { hasAnyPermission, hasPermission, P } from '../../../utils/permissions';
import { employeeDisplayName } from '../../../utils/employeeName';
import type { ConflictConsultant } from '../../../hooks/useConflictsSummary';

interface DashboardAlertsProps {
  /** Conflicted consultants from the shared summary endpoint (item 4, AC-19). */
  conflictConsultants: ConflictConsultant[];
  /** Open projects ending today or within 7 days: the server's `ending-soon` flag. */
  atRiskProjects: ReadonlyArray<{ id: string; name: string }>;
  onNavigateToPlanning?: () => void;
  onNavigateToProjects?: () => void;
}

/**
 * Top-of-dashboard alert strip (Phase 3b). Renders the conflicts +
 * at-risk alerts above ALL sections per the prototype, so urgent
 * information is always visible regardless of which section chips the
 * user has toggled on.
 *
 * Each alert gates internally on the relevant permission key so a user
 * who lacks the permission never sees the alert — even though the
 * containing component renders above sections (which means it isn't
 * gated by any individual section chip).
 *
 * - Conflicts alert: VIEW_REPORTS_OPERATIONAL (planner+, AC-R1 — unchanged
 *   even though the summary *endpoint* itself is gated `view:projects`)
 * - At-risk alert:   MANAGE_PROJECTS or MANAGE_ALLOCATIONS (planner+)
 */
export function DashboardAlerts({
  conflictConsultants,
  atRiskProjects,
  onNavigateToPlanning,
  onNavigateToProjects,
}: DashboardAlertsProps) {
  const { permissions } = usePermissions();

  const showConflicts =
    hasPermission(permissions, P.VIEW_REPORTS_OPERATIONAL) &&
    conflictConsultants.length > 0;

  const showAtRisk =
    hasAnyPermission(permissions, P.MANAGE_PROJECTS, P.MANAGE_ALLOCATIONS) &&
    atRiskProjects.length > 0;

  if (!showConflicts && !showAtRisk) return null;

  return (
    <Stack spacing={1.25} sx={{ mb: 3 }}>
      {showConflicts && (
        <Alert
          icon={<ErrorOutlineIcon fontSize="small" />}
          severity="error"
          variant="outlined"
          sx={(theme) => ({
            borderRadius: 1,
            borderLeftWidth: 3,
            backgroundColor: theme.palette.statusSoft.danger,
            alignItems: 'center',
            '& .MuiAlert-message': { flex: 1, py: 0.25 },
          })}
          action={
            onNavigateToPlanning ? (
              <Link
                component="button"
                type="button"
                onClick={onNavigateToPlanning}
                underline="hover"
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  color: 'error.main',
                }}
              >
                Resolve <ArrowForwardIcon sx={{ fontSize: 12 }} />
              </Link>
            ) : undefined
          }
        >
          <Box component="span" sx={{ fontSize: '0.8125rem' }}>
            <Box component="strong" sx={{ color: 'text.primary' }}>
              {conflictConsultants.length} consultant
              {conflictConsultants.length === 1 ? '' : 's'} with allocation conflicts
            </Box>
            <Box component="span" sx={{ color: 'text.secondary' }}>
              {' · '}
              {conflictConsultants
                .map((c) => employeeDisplayName(c))
                .join(', ')}
            </Box>
          </Box>
        </Alert>
      )}

      {showAtRisk && (
        <Alert
          icon={<WarningAmberIcon fontSize="small" />}
          severity="warning"
          variant="outlined"
          sx={(theme) => ({
            borderRadius: 1,
            borderLeftWidth: 3,
            backgroundColor: theme.palette.statusSoft.warn,
            alignItems: 'center',
            '& .MuiAlert-message': { flex: 1, py: 0.25 },
          })}
          action={
            onNavigateToProjects ? (
              <Link
                component="button"
                type="button"
                onClick={onNavigateToProjects}
                underline="hover"
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  color: 'warning.main',
                }}
              >
                View <ArrowForwardIcon sx={{ fontSize: 12 }} />
              </Link>
            ) : undefined
          }
        >
          <Box component="span" sx={{ fontSize: '0.8125rem' }}>
            <Box component="strong" sx={{ color: 'text.primary' }}>
              {atRiskProjects.length} project
              {atRiskProjects.length === 1 ? '' : 's'} completing within 7 days
            </Box>
            <Box component="span" sx={{ color: 'text.secondary' }}>
              {' · '}
              {atRiskProjects.map((project) => project.name).join(', ')}
            </Box>
          </Box>
        </Alert>
      )}
    </Stack>
  );
}
